import React, { useState, useEffect } from "react";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";

// Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Custom hook for responsive breakpoint
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    
    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [breakpoint]);

  return isMobile;
}

const IconLength = () => (
  <svg className="yacht-types__spec-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);
const IconGuests = () => (
  <svg className="yacht-types__spec-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconCrew = () => (
  <svg className="yacht-types__spec-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconCabins = () => (
  <svg className="yacht-types__spec-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 4v16M2 8h20M2 12h20M6 8v8M6 12h12M18 8v8" />
  </svg>
);
const IconBathrooms = () => (
  <svg className="yacht-types__spec-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6h6v12H9V6z" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

function formatPrice(price, currency) {
  const num = typeof price === "string" ? parseFloat(price) : price;
  const symbol = currency === "THB" || currency == null ? "฿" : currency;
  if (Number.isNaN(num)) return `— ${symbol}`;
  return `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
}

// Extracted Card component for reuse in both Grid and Swiper
const YachtCard = ({ yacht, baseUrl, onSelect }) => (
  <article
    className={`yacht-types__card${yacht.status === "reserved" ? " yacht-types__card--reserved" : ""}`}
    onClick={() => onSelect?.(yacht)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect?.(yacht);
      }
    }}
    role={onSelect ? "button" : undefined}
    tabIndex={onSelect ? 0 : undefined}
    aria-label={onSelect ? `View details for ${yacht.name}` : undefined}
  >
    <div className="yacht-types__card-image-wrap">
      <img
        src={baseUrl + ((yacht.images && yacht.images[0]) || yacht.image)}
        alt={yacht.name}
        className="yacht-types__card-image"
        loading="lazy"
      />
      {yacht.status === "reserved" && (
        <span className="yacht-types__card-chip yacht-types__card-chip--reserved">
          Reserved
        </span>
      )}
    </div>
    <div className="yacht-types__card-body">
      <div className="yacht-types__card-header">
        <h3 className="yacht-types__card-name">{yacht.name}</h3>
      </div>
      <div className="yacht-types__specs">
        <span className="yacht-types__spec">
          <IconLength />
          <span>{yacht.lengthFt} ft</span>
        </span>
        <span className="yacht-types__spec">
          <IconGuests />
          <span>{yacht.guests} Guest{yacht.guests !== 1 ? "s" : ""}</span>
        </span>
        {yacht.crew != null && (
          <span className="yacht-types__spec">
            <IconCrew />
            <span>{yacht.crew} Crew</span>
          </span>
        )}
        {yacht.cabins != null && (
          <span className="yacht-types__spec">
            <IconCabins />
            <span>{yacht.cabins} Cabin{yacht.cabins !== 1 ? "s" : ""}</span>
          </span>
        )}
        {yacht.bathrooms != null && (
          <span className="yacht-types__spec">
            <IconBathrooms />
            <span>{yacht.bathrooms} Bathroom{yacht.bathrooms !== 1 ? "s" : ""}</span>
          </span>
        )}
      </div>
      <div className="yacht-types__card-footer">
        <span className="yacht-types__card-price">
          {yacht.status === "reserved" ? "Reserved" : formatPrice(yacht.price, yacht.currency)}
        </span>
        <button
          type="button"
          className="yacht-types__card-cta"
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(yacht);
          }}
        >
          See more detail
        </button>
      </div>
    </div>
  </article>
);

// Normalize yacht for detail modal: support both legacy (array amenities, optional fields) and new structure (object amenities, no yearBuilt/builder/dimensions/speed)
function withDetailDefaults(yacht) {
  const images = Array.isArray(yacht.images)
    ? yacht.images
    : yacht.image
      ? [yacht.image]
      : [];
  return {
    ...yacht,
    images,
    yearBuilt: yacht.yearBuilt ?? null,
    builder: yacht.builder ?? null,
    beamFt: yacht.beamFt ?? null,
    draftFt: yacht.draftFt ?? null,
    cabins: yacht.cabins ?? null,
    bathrooms: yacht.bathrooms ?? null,
    amenities: yacht.amenities != null ? yacht.amenities : [],
    topSpeed: yacht.topSpeed ?? null,
    cruisingSpeed: yacht.cruisingSpeed ?? null,
    fullDescription: yacht.fullDescription ?? yacht.description ?? "",
  };
}

const DRINK_LABELS = {
  hennessyVSOP: "Hennessy VSOP",
  changUnpasteurized: "Chang Unpasteurized",
  moet: "Moët",
  donJulio1942: "Don Julio 1942",
  donJulioBlanco: "Don Julio Blanco",
};
function drinkKeyToLabel(key) {
  return DRINK_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
}

function YachtDetailModal({ yacht, baseUrl, onClose }) {
  const y = withDetailDefaults(yacht);
  const images = y.images ?? [];
  const [modalTab, setModalTab] = useState("details");
  const yachtPass = y.packages?.yachtPass;
  const hasDrinkPackage = yachtPass != null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };
  const handleContentClick = (e) => e.stopPropagation();

  const slides = images.length > 0 ? images : [null];

  return (
    <div
      className="yacht-detail-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Yacht details: ${y.name}`}
      onClick={handleOverlayClick}
    >
      <div className="yacht-detail-modal" onClick={handleContentClick}>
        <button
          type="button"
          className="yacht-detail-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div className="yacht-detail-modal__image-wrap">
          <Swiper
            className="yacht-detail-modal__main-swiper"
            modules={[Navigation, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
          >
            {slides.map((src, i) => (
              <SwiperSlide key={src ? `${src}-${i}` : `placeholder-${i}`}>
                {src ? (
                  <img
                    src={baseUrl + src}
                    alt={`${y.name} – ${i + 1} of ${images.length}`}
                    className="yacht-detail-modal__image"
                  />
                ) : (
                  <div className="yacht-detail-modal__image-placeholder">
                    {y.name}
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="yacht-detail-modal__body">
          <div className="yacht-detail-modal__header">
            <h3 className="yacht-detail-modal__name">{y.name}</h3>
            <span className="yacht-detail-modal__price">
              {y.status === "reserved" ? "Reserved" : formatPrice(y.price, y.currency)}
            </span>
          </div>
          <div className="yacht-detail-modal__specs">
            <span className="yacht-detail-modal__spec">
              <IconLength />
              <span>{y.lengthFt} ft</span>
            </span>
            <span className="yacht-detail-modal__spec">
              <IconGuests />
              <span>{y.guests} Guest{y.guests !== 1 ? "s" : ""}</span>
            </span>
            {y.crew != null && (
              <span className="yacht-detail-modal__spec">
                <IconCrew />
                <span>{y.crew} Crew</span>
              </span>
            )}
            {y.cabins != null && (
              <span className="yacht-detail-modal__spec">
                <IconCabins />
                <span>{y.cabins} Cabin{y.cabins !== 1 ? "s" : ""}</span>
              </span>
            )}
            {y.bathrooms != null && (
              <span className="yacht-detail-modal__spec">
                <IconBathrooms />
                <span>{y.bathrooms} Bathroom{y.bathrooms !== 1 ? "s" : ""}</span>
              </span>
            )}
          </div>
          <div className="yacht-detail-modal__tabs" role="tablist" aria-label="Yacht details and packages">
            <button
              type="button"
              role="tab"
              aria-selected={modalTab === "details"}
              aria-controls="yacht-detail-modal-panel-details"
              id="yacht-detail-modal-tab-details"
              className={`yacht-detail-modal__tab ${modalTab === "details" ? "yacht-detail-modal__tab--active" : ""}`}
              onClick={() => setModalTab("details")}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={modalTab === "drinks"}
              aria-controls="yacht-detail-modal-panel-drinks"
              id="yacht-detail-modal-tab-drinks"
              className={`yacht-detail-modal__tab ${modalTab === "drinks" ? "yacht-detail-modal__tab--active" : ""}`}
              onClick={() => setModalTab("drinks")}
            >
              Drink package
            </button>
          </div>
          {modalTab === "details" && (
            <div id="yacht-detail-modal-panel-details" role="tabpanel" aria-labelledby="yacht-detail-modal-tab-details">
              <dl className="yacht-detail-modal__details">
                {y.yearBuilt != null && (
                  <div className="yacht-detail-modal__detail-row">
                    <dt>Year built</dt>
                    <dd>{y.yearBuilt}</dd>
                  </div>
                )}
                {y.builder != null && (
                  <div className="yacht-detail-modal__detail-row">
                    <dt>Builder</dt>
                    <dd>{y.builder}</dd>
                  </div>
                )}
                {y.beamFt != null && y.draftFt != null && (
                  <div className="yacht-detail-modal__detail-row">
                    <dt>Dimensions</dt>
                    <dd>Beam {y.beamFt} ft, Draft {y.draftFt} ft</dd>
                  </div>
                )}
                {y.topSpeed != null && y.cruisingSpeed != null && (
                  <div className="yacht-detail-modal__detail-row">
                    <dt>Speed</dt>
                    <dd>Top {y.topSpeed} knots, Cruising {y.cruisingSpeed} knots</dd>
                  </div>
                )}
              </dl>
              {(() => {
                const amenityLabels = Array.isArray(y.amenities)
                  ? y.amenities
                  : typeof y.amenities === "object" && y.amenities !== null
                    ? Object.entries(y.amenities)
                        .filter(([, v]) => v === true)
                        .map(([k]) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim())
                    : [];
                const col1 = amenityLabels.slice(0, 7);
                const col2 = amenityLabels.slice(7, 14);
                return col1.length > 0 || col2.length > 0 ? (
                  <div className="yacht-detail-modal__amenities">
                    <h4 className="yacht-detail-modal__amenities-title">Features</h4>
                    <div className="yacht-detail-modal__amenities-grid">
                      <ul className="yacht-detail-modal__amenities-list">
                        {col1.map((item, i) => (
                          <li key={`${item}-${i}`}>{item}</li>
                        ))}
                      </ul>
                      <ul className="yacht-detail-modal__amenities-list">
                        {col2.map((item, i) => (
                          <li key={`col2-${item}-${i}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
          {modalTab === "drinks" && (
            <div id="yacht-detail-modal-panel-drinks" role="tabpanel" aria-labelledby="yacht-detail-modal-tab-drinks" className="yacht-detail-modal__drinks">
              {hasDrinkPackage ? (
                <>
                  {yachtPass.quantity != null && (
                    <p className="yacht-detail-modal__drinks-quantity">For up to {yachtPass.quantity} guests</p>
                  )}
                  {yachtPass.includedDrinks != null && Object.keys(yachtPass.includedDrinks).length > 0 && (
                    <ul className="yacht-detail-modal__drinks-list">
                      {Object.entries(yachtPass.includedDrinks).map(([key, value]) => (
                        <li key={key}>
                          {drinkKeyToLabel(key)}: {value}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="yacht-detail-modal__drinks-empty">No drink package information.</p>
              )}
            </div>
          )}
          {y.status !== "reserved" && (
            <div className="yacht-detail-modal__footer">
              <button
                type="button"
                className="yacht-detail-modal__book-btn"
                onClick={() => window.open("https://lin.ee/eTmgxtK", "_self")}
              >
                BOOK NOW
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const YachtTypesSection = (props) => {
  const data = props.data ?? {};
  const yachts = data?.yachts ?? [];
  const baseUrl = process.env.PUBLIC_URL || "";
  const isMobile = useIsMobile(768);
  const [selectedYacht, setSelectedYacht] = useState(null);
  const [activeTab, setActiveTab] = useState("available");
  const filteredYachts = yachts.filter((y) => y.status === activeTab);

  useEffect(() => {
    if (selectedYacht == null) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") setSelectedYacht(null);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedYacht]);

  const handleSelectYacht = (yacht) => setSelectedYacht(yacht);
  const handleCloseModal = () => setSelectedYacht(null);

  return (
    <section id="yacht-types" className="yacht-types" aria-label="Yacht types">
      <div className="yacht-types__inner">
        <h2 className="yacht-types__title">YACHT TYPES</h2>

        <div className="yacht-types__tabs" role="tablist" aria-label="Filter by availability">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "available"}
            aria-controls="yacht-types-panel"
            id="yacht-types-tab-available"
            className={`yacht-types__tab ${activeTab === "available" ? "yacht-types__tab--active" : ""}`}
            onClick={() => setActiveTab("available")}
          >
            Available
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "reserved"}
            aria-controls="yacht-types-panel"
            id="yacht-types-tab-reserved"
            className={`yacht-types__tab ${activeTab === "reserved" ? "yacht-types__tab--active" : ""}`}
            onClick={() => setActiveTab("reserved")}
          >
            Reserved
          </button>
        </div>

        <div id="yacht-types-panel" role="tabpanel" aria-labelledby={`yacht-types-tab-${activeTab}`}>
          {filteredYachts.length === 0 ? (
            <p className="yacht-types__empty">No yachts in this category.</p>
          ) : isMobile ? (
            // Mobile: Horizontal card slider
            <div className="yacht-types__slider">
              <Swiper
                key={activeTab}
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={16}
                slidesPerView={1.15}
                centeredSlides={false}
                grabCursor={true}
                loop={filteredYachts.length > 1}
                speed={450}
                autoHeight={false}
                pagination={{ clickable: true }}
              >
                {filteredYachts.map((yacht, index) => (
                  <SwiperSlide key={`${yacht.name}-${index}`}>
                    <YachtCard yacht={yacht} baseUrl={baseUrl} onSelect={handleSelectYacht} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            // Desktop/Tablet: Grid layout
            <div className="yacht-types__grid">
              {filteredYachts.map((yacht, index) => (
                <YachtCard
                  key={`${yacht.name}-${index}`}
                  yacht={yacht}
                  baseUrl={baseUrl}
                  onSelect={handleSelectYacht}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedYacht && (
        <YachtDetailModal
          yacht={selectedYacht}
          baseUrl={baseUrl}
          onClose={handleCloseModal}
        />
      )}
    </section>
  );
};
